import { useState } from "react";
import { CheckCircle2, Phone, MapPin, Clock, Send } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";

const INQUIRY_TYPES = ["Existing Customer Request", "Talk to Sales", "STAT Emergency Courier"];

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#0047FF] focus:ring-2 focus:ring-blue-100 transition-all duration-200";

export const QuoteForm = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", inquiry_type: INQUIRY_TYPES[1], notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/quotes", form);
      setReference(data.reference);
    } catch (err) {
      setError(formatApiError(err, "Could not submit your request. Please call (571) 850-9387."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative bg-[#F8FAFC] py-24 lg:py-32 overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 bg-grid-faint" />
      <div className="absolute -bottom-40 -right-40 h-[480px] w-[480px] rounded-full bg-[#0047FF]/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <Overline>Get A Quote — Get In Touch</Overline>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            Ready to learn how HLM can customize a logistics solution for your business?
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 max-w-lg">
            Fill out the form and one of our local team members will contact you the same day
            to answer your questions and learn more about your needs.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            <a href="tel:5718509387" data-testid="quote-phone-card"
               className="glass rounded-2xl px-6 py-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow duration-300">
              <span className="h-11 w-11 rounded-xl bg-[#0047FF] flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5 text-white" />
              </span>
              <span>
                <span className="block text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-slate-500">24/7 Dispatch Line</span>
                <span className="block font-display text-lg font-bold text-slate-900">(571) 850-9387</span>
              </span>
            </a>
            <div className="glass rounded-2xl px-6 py-5 flex items-center gap-4 shadow-sm" data-testid="quote-hq-card">
              <span className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-[#0047FF]" />
              </span>
              <span>
                <span className="block text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-slate-500">Headquarters</span>
                <span className="block text-sm font-bold text-slate-900">23710 Schooler Plz, Ashburn, VA 20148</span>
              </span>
            </div>
            <div className="glass rounded-2xl px-6 py-5 flex items-center gap-4 shadow-sm" data-testid="quote-region-card">
              <span className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-[#0047FF]" />
              </span>
              <span>
                <span className="block text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-slate-500">Service Region</span>
                <span className="block text-sm font-bold text-slate-900">Washington DC · Maryland · Virginia — 24/7/365</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-[1.75rem] bg-white border border-slate-200/80 shadow-xl shadow-blue-900/10 p-7 sm:p-9">
            {reference ? (
              <div data-testid="quote-success" className="py-10 text-center">
                <span className="mx-auto h-16 w-16 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-slate-900 tracking-tight">Request received.</h3>
                <p className="mt-3 text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
                  Reference <span className="font-mono font-bold text-[#0047FF]">{reference}</span> — a local
                  team member will contact you the same day.
                </p>
                <button data-testid="quote-another-btn" onClick={() => { setReference(null); setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", inquiry_type: INQUIRY_TYPES[1], notes: "" }); }}
                        className="mt-8 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 hover:border-[#0047FF] hover:text-[#0047FF] transition-colors duration-200">
                  Submit another request
                </button>
              </div>
            ) : (
              <form data-testid="quote-form" onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">First name</label>
                    <input data-testid="quote-first-name" required value={form.first_name} onChange={set("first_name")} className={inputCls} placeholder="Jordan" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Last name</label>
                    <input data-testid="quote-last-name" required value={form.last_name} onChange={set("last_name")} className={inputCls} placeholder="Reyes" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
                    <input data-testid="quote-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@facility.org" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Phone</label>
                    <input data-testid="quote-phone" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(571) 555-0100" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Company / Facility</label>
                  <input data-testid="quote-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Fairview Medical Center" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">How can we help you?</label>
                  <select data-testid="quote-inquiry-type" value={form.inquiry_type} onChange={set("inquiry_type")} className={inputCls}>
                    {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Notes</label>
                  <textarea data-testid="quote-notes" rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} placeholder="Pickup location, specimen type, timing needs…" />
                </div>
                {error && <p data-testid="quote-error" className="text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>}
                <button data-testid="quote-submit" type="submit" disabled={submitting}
                        className="group w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#0047FF] px-7 py-4 text-sm font-bold text-white shadow-xl shadow-blue-600/25 hover:bg-[#0037CC] disabled:opacity-60 transition-all duration-200">
                  {submitting ? "Sending…" : "Get in Touch"}
                  <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </button>
                <p className="text-center text-[11px] text-slate-400">Same-day response · Servicing Washington DC, Maryland &amp; Virginia</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
